/* eslint-disable @typescript-eslint/naming-convention */
import {
  Checkbox,
  FormControlLabel,
  FormGroup,
  Menu,
  Stack,
} from '@mui/material';
import IEnterprise from '@enterprise/models/enterprise';
import { DisableDialogContentStyled } from './enterprise-row.styled';
import { useTranslation } from 'react-i18next';
import { StatusChip } from '../../../../components/atoms/StatusChip';
import { useEffect, useState } from 'react';
import {
  useDisableEnterprise,
  useEnableEnterprise,
} from '@enterprise/mutations/enterpriseMutations';
import useJDialog from '@/hooks/useJDialog';
import { JButton } from '@atoms/JButton';
import { UseMutateFunction } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { useNavigate } from 'react-router-dom';
import Paths from '@/routes/paths';
import JTableCell from '@atoms/JTabelCell';
import { ShowMoreButton } from '@atoms/ShowMoreButton';
import { MenuItemStyled } from '@styled/menu-item.styled';
import { TableRowStyled } from '@styled/table-row.styled';

interface Props {
  row: IEnterprise;
}

type disbaleMutationType = UseMutateFunction<
  AxiosResponse<null, any>,
  unknown,
  { id: string; sendEmail: boolean },
  unknown
>;

const EnterpriseRow = ({ row }: Props) => {
  const { t } = useTranslation();
  const {
    enterprise_name,
    enterprise_siren,
    city_zip_code,
    enterprise_deleted_at,
    city_name,
    establishmentscount,
    userscount,
    enterprise_id,
    address_city_name,
  } = row;

  const [rowMenuanchorEl, setRowMenuAnchorEl] = useState<null | HTMLElement>(
    null,
  );
  const [isActive, setIsActive] = useState<boolean>();

  const open = Boolean(rowMenuanchorEl);
  const navigate = useNavigate();
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setRowMenuAnchorEl(event.currentTarget);
  };
  const handleRowMenuClose = () => {
    setRowMenuAnchorEl(null);
  };

  const { mutate: enableEnterpriseMutate, isSuccess: enableSuccess } =
    useEnableEnterprise();
  const { mutate: disableEnterpriseMutate, isSuccess: disableSuccess } =
    useDisableEnterprise();

  const { showJDialog, closeJDialog } = useJDialog();

  const enableEnterprise = (id: string) => {
    handleRowMenuClose();
    enableEnterpriseMutate({ id });
    handleRowMenuClose();
  };
  useEffect(() => {
    setIsActive(!enterprise_deleted_at);
  }, [enterprise_deleted_at]);
  useEffect(() => {
    if (!enableSuccess) return;
    setIsActive(enableSuccess);
  }, [enableSuccess]);
  useEffect(() => {
    if (!disableSuccess) return;
    setIsActive(!disableSuccess);
    closeJDialog();
  }, [disableSuccess]);

  const openDisableDialog = (
    enterpriseName: string,
    enterpriseId: string,
    disableMutation: disbaleMutationType,
  ): void => {
    handleRowMenuClose();
    showJDialog(
      'Désactiver cette entreprise ?',
      <DisableDialogContent
        enterpriseId={enterpriseId}
        enterpriseName={enterpriseName}
        disableMutation={disableMutation}
      />,
    );
  };

  return (
    <TableRowStyled
      key={enterprise_id}
      data-test={`enterprise-list-row-${enterprise_siren}`}
    >
      <JTableCell isbold>{enterprise_name}</JTableCell>
      <JTableCell>{enterprise_siren}</JTableCell>
      <JTableCell>{city_zip_code}</JTableCell>
      <JTableCell>{city_name ?? address_city_name}</JTableCell>
      <JTableCell>
        <StatusChip
          label={isActive ? t('ACTIVE') : t('INACTIVE')}
          variant={isActive ? 'success' : 'danger'}
        ></StatusChip>
      </JTableCell>
      <JTableCell>{establishmentscount}</JTableCell>
      <JTableCell>{userscount}</JTableCell>
      <JTableCell>
        <ShowMoreButton
          data-qa={`navigate-${enterprise_siren}`}
          onClick={handleClick}
          open={open}
        />

        <Menu
          id="basic-menu"
          anchorEl={rowMenuanchorEl}
          open={open}
          onClose={handleRowMenuClose}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <MenuItemStyled
            red={!!isActive}
            green={!isActive}
            data-qa={`action-${enterprise_siren}`}
            onClick={() =>
              isActive
                ? openDisableDialog(
                    row.enterprise_name,
                    enterprise_id,
                    disableEnterpriseMutate,
                  )
                : enableEnterprise(enterprise_id)
            }
          >
            {isActive ? t('DESACTIVATE') : t('ACTIVATE')}
          </MenuItemStyled>
          {isActive && (
            <MenuItemStyled
              red={false}
              green={false}
              data-qa={`navigate-${enterprise_siren}`}
              onClick={() =>
                navigate(Paths.EditEnterprise.replace(':id', enterprise_id))
              }
            >
              Détails de l’entreprise
            </MenuItemStyled>
          )}
        </Menu>
      </JTableCell>
    </TableRowStyled>
  );
};

const DisableDialogContent = ({
  enterpriseName,
  enterpriseId,
  disableMutation,
}: {
  enterpriseName: string;
  enterpriseId: string;
  disableMutation: disbaleMutationType;
}) => {
  const [sendEmail, setSendEmail] = useState<boolean>(false);
  const { closeJDialog } = useJDialog();
  const { t } = useTranslation();
  return (
    <Stack gap={2}>
      <DisableDialogContentStyled>
        {t('DISABLE_ENTERPRISE_DIALOG_TEXT_1')}
        <strong> {enterpriseName}</strong>?{' '}
        {t('DISABLE_ENTERPRISE_DIALOG_TEXT_2')}
      </DisableDialogContentStyled>
      <FormGroup>
        <FormControlLabel
          control={
            <Checkbox onChange={(e) => setSendEmail(e.target.checked)} />
          }
          label={t('DISABLE_ENTERPRISE_OPTION_1')}
        />
        {/* <FormControlLabel
          control={<Checkbox />}
          label={t('DISABLE_ENTERPRISE_OPTION_2')}
        /> */}
      </FormGroup>
      <Stack direction={'row'} gap={1}>
        <JButton
          data-qa={`disable`}
          onClick={() => disableMutation({ id: enterpriseId, sendEmail })}
          variant="contained"
          color="error"
        >
          {t('DISABLE_ENTERPRISE_DISABLE_BUTTON')}
        </JButton>
        <JButton
          data-qa={`close`}
          onClick={closeJDialog}
          variant="contained"
          color="info"
        >
          {t('CANCEL')}
        </JButton>
      </Stack>
    </Stack>
  );
};

export default EnterpriseRow;
